import { Box, Button } from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { TransactionProvider } from '../../context/TransactionContext';
import { ApiResultMeta } from '../../services/general.types';
import {
    Transaction,
    TransactionIssueType,
    TransactionsFilterData,
    TransactionTypeGenericType,
} from '../../services/transaction.types';
import PageLayout from '../common/PageLayout';
import BulkActionBar from './actions/BulkActionBar';
import { useTransactionMutations } from './hooks/useTransactionMutations';
import { useQuery } from '@tanstack/react-query';
import { useTransactionService } from '../../services/transaction.service';
import { useTransactionQueries } from './hooks/useTransactionQueries';
import { useTransactionState } from './hooks/useTransactionState';
import TransactionModal from './modals/TransactionModal/TransactionModal';
import TransactionTable from './table/TransactionTable';
import TransactionStatusBar from './components/TransactionStatusBar';
import {
    processNonTaxableTransactions,
    processNotSpamTransactions,
    processSpamTransactions,
    processTaxableTransactions,
} from './utils/transactionUtils';

const TransactionPageContent: React.FC = () => {
    const transactionService = useTransactionService();
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const {
        saveTransaction,
        deleteTransactions,
        markTransactionsAsSpam,
        markTransactionsAsNotSpam,
        markNonTaxable,
        groupSaveTransaction,
        isSaving,
        isDeleting,
    } = useTransactionMutations();
    const {
        selectedIds,
        setSelectedIds,
        transactionModalOpen,
        setTransactionModalOpen,
        selectedTransaction,
        setSelectedTransaction,
        filters,
        setFilters,
        isDetailedView,
        setIsDetailedView,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
    } = useTransactionState();

    const memoizedFilters = useMemo<TransactionsFilterData>(() => {
        const filterData: TransactionsFilterData = {
            search: filters.search,
            types: filters.types,
            accounts: filters.accounts,
            coins: filters.assets,
        };

        // Handle reviewTypes (status filter) specially
        // If multiple flags are selected, we'll make separate requests for each flag
        // and merge the results on the frontend side
        if (filters.flags?.length === 1) {
            // If only one status is selected, use the regular approach
            filterData.reviewTypes = filters.flags;
        }
        // Note: If multiple flags are selected, we'll handle it in the useTransactionQueries hook
        // by making multiple requests and merging the results

        // Only add dates if they exist
        if (filters.dates?.start) {
            filterData.startDate = format(new Date(filters.dates.start), 'yyyy-MM-dd');
        }
        if (filters.dates?.end) {
            filterData.endDate = format(new Date(filters.dates.end), 'yyyy-MM-dd');
        }

        return filterData;
    }, [
        filters.search,
        filters.types,
        filters.accounts,
        filters.assets,
        filters.dates?.start,
        filters.dates?.end,
        filters.flags,
    ]);

    // Store the flags filter separately to handle OR logic in the frontend
    const statusFilters = useMemo(() => filters.flags || [], [filters.flags]);

    const {
        transactionResponse,
        transactionTypes = [],
        isLoading,
        isFetching,
    } = useTransactionQueries(page, rowsPerPage, order, orderBy, memoizedFilters, statusFilters);

    // Handlers
    const handleFiltersChange = async (newFilters: TransactionsFilterData) => {
        setFilters(newFilters);
        setPage(0);
    };

    const handleRowSelect = (ids: number[]) => {
        setSelectedIds(ids);
    };

    const handleRowClick = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setTransactionModalOpen(true);
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        deleteTransactions(selectedIds, {
            onSuccess: () => {
                showNotification(t('transactions.notifications.deleted'));
                handleClearSelection();
            },
            onError: () => {
                showNotification(t('transactions.notifications.deleteError'));
            },
        });
    };

    const handleMarkAsSpam = (transactions: Transaction[]) => {
        const spamParams = processSpamTransactions(transactions);

        if (spamParams.length === 0) {
            showNotification(t('transactions.notifications.noValidSpam'));
            return;
        }

        markTransactionsAsSpam(spamParams, {
            onSuccess: () => {
                showNotification(t('transactions.notifications.markedAsSpam'));
                handleClearSelection();
            },
            onError: () => {
                showNotification(t('transactions.notifications.markAsSpamError'));
            },
        });
    };

    const handleMarkAsSpamBulk = () => {
        if (selectedIds.length === 0) return;
        handleMarkAsSpam(getSelectedTransactions());
    };

    const handleMarkAsSpamSingleTransaction = (transaction: Transaction) => {
        handleMarkAsSpam([transaction]);
    };

    const handleMarkNonTaxable = (transactions: Transaction[]) => {
        const nonTaxableParams = processNonTaxableTransactions(transactions);

        if (nonTaxableParams.length === 0) {
            showNotification(t('transactions.notifications.noValidNonTaxable'));
            return;
        }

        markNonTaxable(nonTaxableParams, {
            onSuccess: () => {
                showNotification(t('transactions.notifications.markedAsNonTaxable'));
                handleClearSelection();
            },
            onError: () => {
                showNotification(t('transactions.notifications.markAsNonTaxableError'));
            },
        });
    };

    const handleMarkNonTaxableBulk = () => {
        if (selectedIds.length === 0) return;
        handleMarkNonTaxable(getSelectedTransactions());
    };

    const handleMarkNonTaxableSingleTransaction = (transaction: Transaction) => {
        handleMarkNonTaxable([transaction]);
    };

    const handleMarkAsNotSpam = (transactions: Transaction[]) => {
        const notSpamParams = processNotSpamTransactions(transactions);

        if (notSpamParams.length === 0) {
            showNotification(t('transactions.notifications.noValidNotSpam'));
            return;
        }

        markTransactionsAsNotSpam(notSpamParams, {
            onSuccess: () => {
                showNotification(t('transactions.notifications.markedAsNotSpam'));
                handleClearSelection();
            },
            onError: () => {
                showNotification(t('transactions.notifications.markAsNotSpamError'));
            },
        });
    };

    const handleMarkAsNotSpamBulk = () => {
        if (selectedIds.length === 0) return;
        handleMarkAsNotSpam(getSelectedTransactions());
    };

    const handleMarkAsNotSpamSingleTransaction = (transaction: Transaction) => {
        handleMarkAsNotSpam([transaction]);
    };

    const handleMarkTaxable = (transactions: Transaction[]) => {
        const taxableParams = processTaxableTransactions(transactions);

        if (taxableParams.length === 0) {
            showNotification(t('transactions.notifications.noValidTaxable'));
            return;
        }

        markNonTaxable(taxableParams, {
            onSuccess: () => {
                showNotification(t('transactions.notifications.markedAsTaxable'));
                handleClearSelection();
            },
            onError: () => {
                showNotification(t('transactions.notifications.markAsTaxableError'));
            },
        });
    };

    const handleMarkTaxableBulk = () => {
        if (selectedIds.length === 0) return;
        handleMarkTaxable(getSelectedTransactions());
    };

    const handleMarkTaxableSingleTransaction = (transaction: Transaction) => {
        handleMarkTaxable([transaction]);
    };

    const handleClearSelection = () => {
        setSelectedIds([]);
    };

    const handleTypeChange = (typeId: number) => {
        if (getSelectedTransactionsGenericType() === TransactionTypeGenericType.Mixed) {
            showNotification(t('transactions.notifications.cannotChangeMixedTypes'));
            return;
        }

        groupSaveTransaction(
            { ids: selectedIds, typeId },
            {
                onSuccess: () => {
                    showNotification(t('transactions.notifications.typesUpdated'));
                    handleClearSelection();
                },
                onError: () => {
                    showNotification(t('transactions.notifications.updateTypesError'));
                },
            }
        );
    };

    const handleTransactionSubmit = (transaction: Partial<Transaction>) => {
        setTransactionModalOpen(false);
        saveTransaction(transaction, {
            onSuccess: () => {
                showNotification(t('transactions.notifications.saved'));
                handleClearSelection();
            },
            onError: () => {
                showNotification(t('transactions.notifications.saveError'));
            },
        });
    };

    const isApiCallInProgress = isLoading || isSaving || isFetching || isDeleting;
    const transactions: Transaction[] = transactionResponse?.data || [];
    const transactionPage: ApiResultMeta | null = transactionResponse?.meta || null;

    const getSelectedTransactions = () => {
        return transactions.filter((tx) => selectedIds.includes(tx.id as number));
    };

    const getSelectedTransactionsGenericType = useCallback(() => {
        const transactions = getSelectedTransactions();
        if (transactions.length === 0) return null;
        if (transactions.every((tx) => tx.type.in && tx.type.out)) return TransactionTypeGenericType.Trade;
        if (transactions.every((tx) => tx.type.in && !tx.type.out)) return TransactionTypeGenericType.In;
        if (transactions.every((tx) => tx.type.out && !tx.type.in)) return TransactionTypeGenericType.Out;
        return TransactionTypeGenericType.Mixed;
    }, [transactions, selectedIds]);

    const getTransactionIssues = useCallback(() => {
        return getSelectedTransactions().every(
            (tx) =>
                tx.issueTypes && 
                tx.issueTypes.length > 0 && 
                !tx.issueTypes.includes(TransactionIssueType.NoIssue) && 
                !tx.issueTypes.includes(TransactionIssueType.MatchedTransfer)
        );
    }, [transactions, selectedIds]);
    
    // Get total transaction count using the same endpoint as tax reports page
    const { 
        data: totalTransactionCount = 0,
        isLoading: isCountLoading 
    } = useQuery({
        queryKey: ['transactionCount'],
        queryFn: async () => {
            try {
                const response = await transactionService.getCount();
                return response.count || 0;
            } catch (error) {
                console.error('Error fetching transaction count:', error);
                throw error;
            }
        },
        retry: 1
    });

    // Get issue counts using the same endpoint as tax reports page
    const { 
        data: issueCountData = { 
            missingAssetValueCount: 0, 
            costBasisCount: 0, 
            spamCoinCount: 0
        }, 
        isLoading: isIssueCountLoading 
    } = useQuery({
        queryKey: ['allTimeIssueCount'],
        queryFn: () => transactionService.getAllTimeIssueCount(),
    });

    // Calculate total issue count from the issue count data
    const totalIssueCount = useMemo(() => {
        return (
            (issueCountData?.missingAssetValueCount || 0) + 
            (issueCountData?.costBasisCount || 0)
        );
    }, [issueCountData]);

    return (
        <PageLayout title={t('transactions.title')}>
            <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                width: '100%',
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '100%', md: '1400px', lg: '1600px' },
                    mx: 'auto',
                    mb: 1
                }}>
                    <Box sx={{ flex: 1 }}>
                        <TransactionStatusBar 
                            showActionButton={false}
                            transactionCount={totalTransactionCount}
                            warningCount={totalIssueCount}
                            totalCount={totalTransactionCount}
                            isLoading={isCountLoading || isIssueCountLoading}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedTransaction(null);
                            setTransactionModalOpen(true);
                        }}
                        sx={{ ml: 2 }}
                    >
                        {t('transactions.buttons.newTransaction')}
                    </Button>
                </Box>
                {/* FilterPanel moved into TransactionTable */}
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    mt: 0,
                    width: '100%'
                }}>
                    {selectedIds.length > 0 && (
                        <BulkActionBar
                            selectedIds={selectedIds}
                            selectedTransactionGenericTypes={getSelectedTransactionsGenericType()}
                            onDelete={handleBulkDelete}
                            onMarkAsSpam={handleMarkAsSpamBulk}
                            onMarkAsNotSpam={handleMarkAsNotSpamBulk}
                            onMarkTaxable={handleMarkTaxableBulk}
                            onMarkNonTaxable={handleMarkNonTaxableBulk}
                            onClearSelection={handleClearSelection}
                            onTypeChange={handleTypeChange}
                            types={transactionTypes}
                            hasIssues={getTransactionIssues()}
                            selectedTransactions={transactions.filter((t) => selectedIds.includes(t.id as number))}
                        />
                    )}
                    <Box id="transaction-table-container" sx={{ 
                        flex: 1, 
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '100%', md: '1400px', lg: '1600px' },
                        mx: 'auto',
                        overflow: 'auto',
                        pb: selectedIds.length > 0 ? 9 : 2,
                        '&::-webkit-scrollbar': {
                            height: 8,
                            width: 8,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            borderRadius: 4,
                        },
                    }}>
                        <TransactionTable
                            transactions={transactions}
                            selectedIds={selectedIds}
                            onRowSelect={handleRowSelect}
                            onRowClick={handleRowClick}
                            isDetailedView={isDetailedView}
                            order={order}
                            orderBy={orderBy}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onOrderChange={setOrder}
                            onOrderByChange={setOrderBy}
                            onPageChange={setPage}
                            onRowsPerPageChange={setRowsPerPage}
                            total={transactionPage?.totalItems || transactions.length}
                            isLoading={isApiCallInProgress}
                            onTransactionEdit={(transaction) => {
                                setSelectedTransaction(transaction as Transaction);
                                setTransactionModalOpen(true);
                                }}
                            onTransactionDelete={(transaction) => {
                                deleteTransactions([transaction.id as number], {
                                    onSuccess: () => {
                                        showNotification(t('transactions.notifications.singleDeleted'));
                                        handleClearSelection();
                                    },
                                    onError: () => {
                                        showNotification(t('transactions.notifications.singleDeleteError'));
                                    },
                                });
                            }}
                            onTransactionSpam={handleMarkAsSpamSingleTransaction}
                            onTransactionNonTaxable={handleMarkNonTaxableSingleTransaction}
                            onTransactionNotSpam={handleMarkAsNotSpamSingleTransaction}
                            onTransactionTaxable={handleMarkTaxableSingleTransaction}
                            onActionComplete={showNotification}
                            onFiltersChange={handleFiltersChange}
                            initialFilters={filters}
                            onViewChange={setIsDetailedView}
                        />
                    </Box>
                </Box>
                <TransactionModal
                    open={transactionModalOpen}
                    onClose={() => setTransactionModalOpen(false)}
                    onSubmit={handleTransactionSubmit}
                    transaction={selectedTransaction}
                />
            </Box>
        </PageLayout>
    );
};

const TransactionPage: React.FC = () => {
    return (
        <TransactionProvider>
            <TransactionPageContent />
        </TransactionProvider>
    );
};

export default TransactionPage;
